import { supabase } from '../supabase/supabaseClient';
import { logger } from '../utils/logUtils';
import { securityMonitoring } from '../security/securityLogging';

/**
 * Update the last_login timestamp for a user's role
 * @param userId The user ID
 * @param role The role to update (defaults to 'user')
 */
export const updateLastLogin = async (userId: string, role: string = 'user'): Promise<void> => {
  try {
    const { error } = await supabase
      .from('user_roles')
      .update({
        last_login: new Date().toISOString(),
        failed_login_attempts: 0
      })
      .eq('user_id', userId)
      .eq('role', role);

    if (error) {
      logger.error('Error updating last_login:', error);
      return;
    }

    logger.info(`Updated last_login for user ${userId} with role ${role}`);
  } catch (err) {
    logger.error('Exception updating last_login:', err);
  }
};

/**
 * Update all roles' last_login for a user and reset failed login attempts
 * Uses RPC function to bypass RLS restrictions
 * @param userId The user ID
 */
export const updateAllRolesLastLogin = async (userId: string): Promise<void> => {
  try {
    const { error: rpcError } = await supabase
      .rpc('reset_failed_attempts_on_login', { p_user_id: userId });

    if (rpcError) {
      logger.error('Error calling reset_failed_attempts_on_login:', rpcError);
      return;
    }

    logger.info(`Updated last_login and reset failed attempts for user ${userId}`);
  } catch (err) {
    logger.error('Exception updating last_login for all roles:', err);
  }
};

/**
 * Increment failed login attempts for a user
 * @param email The user's email address
 */
export const incrementFailedLoginAttempts = async (email: string): Promise<void> => {
  try {
    const { error: rpcError } = await supabase
      .rpc('increment_failed_login_by_email', { p_email: email });

    if (rpcError) {
      logger.error('Error calling increment_failed_login_by_email:', rpcError);
      return;
    }

    await securityMonitoring.logEvent(
      securityMonitoring.eventTypes.LOGIN_FAILURE,
      { email },
      'warning'
    );

    logger.info(`Incremented failed login attempts for user with email ${email}`);
  } catch (err) {
    logger.error('Exception incrementing failed login attempts:', err);
  }
};

/**
 * Get the current failed login attempt count for a user
 * @param email The user's email address
 * @returns Promise<number> The number of failed attempts
 */
export const getFailedLoginAttempts = async (email: string): Promise<number> => {
  try {
    const { data, error } = await supabase
      .rpc('get_failed_login_attempts', { p_email: email });

    if (error) {
      logger.error('Error getting failed login attempts:', error);
      return 0;
    }

    return data || 0;
  } catch (err) {
    logger.error('Exception getting failed login attempts:', err);
    return 0;
  }
};

/**
 * Check if a user account should be locked due to too many failed attempts
 * Uses RPC function to bypass RLS restrictions
 * @param email The user's email address
 * @returns Promise<boolean> True if account should be locked
 */
export const shouldLockAccount = async (email: string): Promise<boolean> => {
  try {
    const { data: isLocked, error: rpcError } = await supabase
      .rpc('check_account_locked', { p_email: email });

    if (rpcError) {
      logger.error('Error calling check_account_locked:', rpcError);
      return false;
    }

    if (isLocked) {
      logger.info(`Account is locked for email: ${email}`);
      await securityMonitoring.logEvent(
        'account_lock_check',
        { email, isLocked: true },
        'warning'
      );
    }

    return isLocked || false;
  } catch (err) {
    logger.error('Exception checking account lock status:', err);
    return false;
  }
};

/**
 * Reset failed login attempts for a user (admin function)
 * @param userId The user ID
 */
export const resetFailedLoginAttempts = async (userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('user_roles')
      .update({ failed_login_attempts: 0 })
      .eq('user_id', userId);

    if (error) {
      logger.error('Error resetting failed login attempts:', error);
      return false;
    }

    await securityMonitoring.logEvent(
      'account_unlocked',
      { userId },
      'info'
    );

    logger.info(`Reset failed login attempts for user ${userId}`);
    return true;
  } catch (err) {
    logger.error('Exception resetting failed login attempts:', err);
    return false;
  }
};